import { readonly } from 'vue';
import { useStorage, watchOnce } from '@vueuse/core';
import { useCallback, useQueryV2 } from '@libs/use';

import * as API from '../requests';
import {
  SYMBOL_QUERY_KEYS,
  INITIAL_DEDUCTION_INSTRUMENT_TYPE_CODE,
  INITIAL_DEDUCTION_SUB_SECTOR_CODE,
} from '../constants';

export type DeductionsStoreType = {
  buyMaximumValue: number;
  buyValue: number;
  exchangeCode: string | null;
  instrumentTypeCodesField: string[] | null;
  sellMaximumValue: number;
  sellValue: number;
  sectorCodesField: string[] | null;
  subSectorCodesField: string[] | null;
};

const deductionsStore = useStorage<DeductionsStoreType[]>('symbol-deductions', [
  {
    buyMaximumValue: 0,
    buyValue: 0,
    exchangeCode: null,
    instrumentTypeCodesField: null,
    sellMaximumValue: 0,
    sellValue: 0,
    sectorCodesField: null,
    subSectorCodesField: null,
  },
]);

export default function useSymbolDeductions() {
  const onReady = useCallback();

  const { execute: getSymbolDeductions } = useQueryV2(API.getSymbolDeductions, {
    key: SYMBOL_QUERY_KEYS.symbolDeduction,
    staleTime: 1000 * 60 * 5, // 5m
    cacheTime: 1000 * 60 * 60 * 24, // 1d
    onSuccess(result) {
      if (result?.data.items?.length) {
        deductionsStore.value = result.data.items;
      }
    },
  });

  watchOnce([deductionsStore, onReady.callback], ([deductions, callback]) => {
    if (deductions.length && callback) {
      onReady.trigger();
    }
  });

  const findWageConfigBySymbol = (symbol): DeductionsStoreType => {
    const deductionsByType = deductionsStore.value.filter((deduction) => {
      return deduction.instrumentTypeCodesField?.includes(
        symbol?.instrumentTypeCode?.toString() ||
          symbol?.instrumentType?.toString() ||
          INITIAL_DEDUCTION_INSTRUMENT_TYPE_CODE
      );
    });

    const wage = deductionsByType?.find((deduction) =>
      deduction?.subSectorCodesField?.includes(
        symbol?.subSectorCode?.toString() || INITIAL_DEDUCTION_SUB_SECTOR_CODE
      )
    );

    return wage ?? deductionsByType?.[0] ?? deductionsStore.value[0];
  };

  return {
    getSymbolDeductions,
    findWageConfigBySymbol,
    onDeductionsReady: onReady.on,
    deductions: readonly(deductionsStore),
  };
}
